/**
 * Trial balance — `/ledger/trial-balance`.
 *
 * This is the closing position as of a period end, not activity within the
 * period: the server sums period 1 through the selected period of that fiscal
 * year. The heading says so, because "trial balance for August" means two
 * different things to two different accountants and only one of them is what
 * this endpoint answers.
 *
 * Each account appears on one side — `debit = max(net,0)`, `credit =
 * max(-net,0)` — so the screen prints the figures it is given and never
 * derives a side of its own. The report is company-wide by contract: there is
 * no branch filter to offer, and pretending otherwise would misstate what the
 * numbers cover.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { MoneyDisplay } from "@/components/money/money-display";
import { localizedName } from "@/lib/i18n";
import { formatCalendarDate } from "@/lib/format";

import {
  getTrialBalance,
  listFiscalYears,
  type FiscalYear,
  type TrialBalance,
} from "../calendar-api";
import { ledgerKeys } from "../queries";

const CCY = /^[A-Z]{3}$/;

export function TrialBalancePage() {
  const { t, i18n } = useTranslation();
  const [periodId, setPeriodId] = useState<string>("");
  const [currency, setCurrency] = useState("");
  const [includeZero, setIncludeZero] = useState(false);

  const years = useQuery<FiscalYear[]>({
    queryKey: ledgerKeys.fiscalYears.all,
    queryFn: ({ signal }) => listFiscalYears(signal),
  });

  // Default to the period the company is actually in, rather than making the
  // operator choose before seeing anything. Falling back to the last generated
  // year keeps a future-dated calendar usable.
  const current = useMemo(() => {
    const all = years.data ?? [];
    const today = new Date().toISOString().slice(0, 10);
    const containing = all.find((year) => year.starts_on <= today && today <= year.ends_on);
    return containing ?? all[all.length - 1];
  }, [years.data]);

  useEffect(() => {
    if (periodId !== "" || current === undefined) return;
    const today = new Date().toISOString().slice(0, 10);
    const ordinary = current.periods.filter((period) => !period.is_adjustment);
    const chosen =
      ordinary.find((period) => period.starts_on <= today && today <= period.ends_on) ??
      ordinary[ordinary.length - 1] ??
      current.periods[0];
    if (chosen !== undefined) setPeriodId(chosen.id);
  }, [current, periodId]);

  const report = useQuery<TrialBalance>({
    queryKey: ledgerKeys.trialBalance(periodId, currency, includeZero),
    queryFn: ({ signal }) =>
      getTrialBalance(
        periodId,
        {
          ...(CCY.test(currency) ? { currency } : {}),
          ...(includeZero ? { include_zero: true } : {}),
        },
        signal,
      ),
    enabled: periodId !== "",
  });

  const day = (iso: string): string => {
    try {
      return formatCalendarDate(iso, i18n.language);
    } catch {
      return iso;
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold tracking-tight">
          {t("ledger.trialBalance.title", "Trial balance")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t(
            "ledger.trialBalance.subtitle",
            "Closing balances as of the end of the selected period, for the whole company.",
          )}
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="tb-period">{t("ledger.trialBalance.period", "As of period")}</Label>
          <Select value={periodId} onValueChange={setPeriodId}>
            <SelectTrigger id="tb-period" className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(years.data ?? []).map((year) =>
                year.periods.map((period) => (
                  <SelectItem key={period.id} value={period.id}>
                    {year.code} · {period.name}
                  </SelectItem>
                )),
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tb-currency">
            {t("ledger.trialBalance.currency", "Transaction currency")}
          </Label>
          <Input
            id="tb-currency"
            dir="ltr"
            maxLength={3}
            className="w-28 font-mono uppercase"
            placeholder={t("ledger.trialBalance.baseCurrency", "Base")}
            value={currency}
            onChange={(event) => setCurrency(event.target.value.toUpperCase())}
          />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Checkbox
            id="tb-zero"
            checked={includeZero}
            onCheckedChange={(checked) => setIncludeZero(checked === true)}
          />
          <Label htmlFor="tb-zero">
            {t("ledger.trialBalance.includeZero", "Include zero balances")}
          </Label>
        </div>
      </div>

      {report.isLoading ? (
        <p className="text-sm text-muted-foreground">{t("common.loading", "Loading…")}</p>
      ) : report.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {t("ledger.trialBalance.error", "The trial balance could not be read.")}
        </p>
      ) : report.data === undefined ? null : (
        <>
          <p className="text-sm text-muted-foreground">
            {t("ledger.trialBalance.asOf", "Cumulative to {{date}} · {{basis}} · {{currency}}", {
              date: day(report.data.fiscal_period.ends_on),
              basis: t(
                `ledger.balanceBasis.${report.data.balance_basis}`,
                report.data.balance_basis,
              ),
              currency: report.data.presentation_currency,
            })}
          </p>

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full min-w-[44rem] text-sm">
                <thead>
                  <tr className="border-b text-xs uppercase text-muted-foreground">
                    <th scope="col" className="p-3 text-start">
                      {t("ledger.trialBalance.account", "Account")}
                    </th>
                    <th scope="col" className="p-3 text-end">
                      {t("ledger.trialBalance.debit", "Debit")}
                    </th>
                    <th scope="col" className="p-3 text-end">
                      {t("ledger.trialBalance.credit", "Credit")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.data.rows.map((row) => (
                    <tr key={row.account.id} className="border-b last:border-0">
                      <td className="p-3">
                        {/* The gap lives on the flex row, not on the LTR-isolated
                            code span, whose own direction would put it on the
                            wrong side in an RTL table. */}
                        <span className="flex items-baseline gap-2">
                          <span dir="ltr" className="font-mono text-xs text-muted-foreground">
                            {row.account.code}
                          </span>
                          <span>
                            {localizedName(row.account.name, row.account.name_ar, i18n.language)}
                          </span>
                        </span>
                      </td>
                      <td className="p-3 text-end">
                        <MoneyDisplay value={row.debit} display="none" />
                      </td>
                      <td className="p-3 text-end">
                        <MoneyDisplay value={row.credit} display="none" />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 font-semibold">
                    <td className="p-3">{t("ledger.trialBalance.total", "Total")}</td>
                    <td className="p-3 text-end">
                      <MoneyDisplay value={report.data.total_debit} />
                    </td>
                    <td className="p-3 text-end">
                      <MoneyDisplay value={report.data.total_credit} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
